import { useRef, useEffect, useState } from "react";
import Style from "./UserProfilePage.module.css";
import { NSPanel, NSButtonBlue, NSBoxDate, NSBoxPhone, NSBoxString, NSBoxEmail, NSSection, NSSpace, NSSpaceSizeType, NSRow, NSColumn, NSLoading } from 'namirasoft-site-react';
import { NSARouterMakerResult } from "namirasoft-account-react";
import { AccountRouterProps } from "../AccountRouterProps";
import { AccountLayout } from "../AccountLayout";
import { AccountRoutes } from "../AccountRoutes";
import { UserRow } from "namirasoft-account";
import { TimeOperation } from "namirasoft-core";

interface UserProfilePageProps extends NSARouterMakerResult, AccountRouterProps { }

interface UserProfilePageState
{
    user: UserRow | null;
}

function UserProfilePage(props: UserProfilePageProps) 
{
    const NSBoxString_FirstName = useRef<NSBoxString>(null);
    const NSBoxString_LastName = useRef<NSBoxString>(null);
    const NSBoxEmail_Email = useRef<NSBoxEmail>(null);
    const NSBoxPhone_Phone = useRef<NSBoxPhone>(null);
    const NSBoxDate_Birthday = useRef<NSBoxDate>(null);

    let [state, setState] = useState<UserProfilePageState>({ user: null });

    useEffect(() =>
    {
        props.account.server.user.Current().then((user) =>
        {
            NSBoxString_FirstName.current?.setValue(user.first_name);
            NSBoxString_LastName.current?.setValue(user.last_name);
            NSBoxEmail_Email.current?.setValue(user.email);
            NSBoxPhone_Phone.current?.setValue(user.phone_temporary);
            NSBoxDate_Birthday.current?.setValue(user.birthday ? new Date(user.birthday) : null);
            setState((prev) => ({ ...prev, user }));
        }).catch(() => { });
        // eslint-disable-next-line react-hooks/exhaustive-deps 
    }, []);

    function onApply(onFinished: () => void)
    {
        try
        {
            let first_name: string = NSBoxString_FirstName.current?.getValue() ?? "";
            let last_name: string = NSBoxString_LastName.current?.getValue() ?? "";
            let email: string = NSBoxEmail_Email.current?.getValue() ?? "";
            let phone: string = NSBoxPhone_Phone.current?.getValue() ?? "";
            let _birthday: Date | null = NSBoxDate_Birthday.current?.getValue() ?? null;
            let birthday: string | null = _birthday ? TimeOperation.format(_birthday, "yyyy-MM-DD") : null;
            props.account.server.user.Update({ first_name, last_name, email, phone, birthday }).then((user) =>
            {
                setState((prev) => ({ ...prev, user }));
                props.notifier.onSuccess("User information has been edited successfully.");
                onFinished();
            }).catch(onFinished);
        } catch (error)
        {
            props.notifier.onError(error as Error);
            onFinished();
        }
    }

    return (
        <AccountLayout
            {...props}
            action={{
                title: 'Profile',
                description: 'Manage and update your personal information to stay secure and up-to-date.',
                menus: {
                    "1": [
                        {
                            id: "1",
                            onClick: {
                                action: onApply
                            },
                            isActive: () => true,
                            menu_item: "Apply"
                        }
                    ]
                }
            }}
        >
            <>
            {
                state.user ? <></> : <NSLoading />
            }
            <div style={{ display: state.user ? "block" : "none" }}>
                <NSSection >
                    <NSPanel style={{ justifyContent: "flex-start" }}>
                        <NSRow>
                            <NSBoxString
                                required={true}
                                title="First Name"
                                ref={NSBoxString_FirstName}
                            />
                            <NSBoxString
                                required={true}
                                title="Last Name"
                                ref={NSBoxString_LastName}
                            />
                        </NSRow>
                        <NSRow>
                            <NSBoxEmail
                                title="Email"
                                ref={NSBoxEmail_Email}
                                required={true}
                                disabled={state.user?.e_verified}
                            />
                            {
                                state.user?.e_verified
                                    ?
                                    <figure className={Style.ns_verified_label}>
                                        <img src="https://static.namirasoft.com/image/concept/verify/green.svg" width={24} height={24} alt="verify" />
                                        <figcaption className="nsa-color-green ms-2">Email address is verified.</figcaption>
                                    </figure>
                                    :
                                    <NSButtonBlue
                                        title="Verify Email"
                                        onClick={{
                                            action: async (onFinished) =>
                                            {
                                                let email: string;
                                                try
                                                {
                                                    // Input validation errors are not reported by the server, so notify here.
                                                    email = NSBoxEmail_Email.current?.getValue() ?? "";
                                                } catch (error)
                                                {
                                                    props.notifier.onError(error as Error);
                                                    onFinished();
                                                    return;
                                                }
                                                try
                                                {
                                                    if (state.user?.email !== email)
                                                        await props.account.server.email.Set({ email });
                                                    props.url.redirect(AccountRoutes.VerifyEmail, {});
                                                } catch
                                                {
                                                    // The server already reported the error through the notifier.
                                                    onFinished();
                                                }
                                            }
                                        }}
                                        loading={{
                                            style: { width: "auto", padding: "2.5rem 0 0" }
                                        }}
                                        classList={[Style.ns_verify_button]}
                                    />
                            }
                        </NSRow>
                        <NSRow style={{ justifyContent: "flex-start" }}>
                            <NSBoxPhone
                                ref={NSBoxPhone_Phone}
                                required={true}
                                title="Phone"
                            />
                            {
                                state.user?.p_verified && state.user.phone === state.user.phone_temporary
                                    ?
                                    <figure className={Style.ns_verified_label}>
                                        <img src="https://static.namirasoft.com/image/concept/verify/green.svg" width={24} height={24} alt="verify" />
                                        <figcaption className="nsa-color-green ms-2">Phone number is verified.</figcaption>
                                    </figure>
                                    :
                                    <NSButtonBlue
                                        title="Verify Phone"
                                        onClick={{
                                            action: async (onFinished) =>
                                            {
                                                let phone: string;
                                                try
                                                {
                                                    // Input validation errors are not reported by the server, so notify here.
                                                    phone = NSBoxPhone_Phone.current?.getValue() ?? "";
                                                } catch (error)
                                                {
                                                    props.notifier.onError(error as Error);
                                                    onFinished();
                                                    return;
                                                }
                                                try
                                                {
                                                    if (state.user?.phone_temporary !== phone)
                                                        await props.account.server.phone.Set({ phone });
                                                    props.url.redirect(AccountRoutes.VerifyPhone, {});
                                                } catch
                                                {
                                                    // The server already reported the error through the notifier.
                                                    onFinished();
                                                }
                                            }
                                        }}
                                        loading={{
                                            style: { width: "auto", padding: "2.5rem 0 0" }
                                        }}
                                        classList={[Style.ns_verify_button]}
                                    />
                            }
                        </NSRow>
                        <NSColumn>
                            <NSBoxDate
                                required={true}
                                title="Birthday"
                                ref={NSBoxDate_Birthday}
                            />
                        </NSColumn>
                    </NSPanel>
                    <NSSpace size={NSSpaceSizeType.SMALL}></NSSpace>
                    <NSButtonBlue
                        title="Apply"
                        onClick={{
                            action: (onFinished) => onApply(onFinished),
                        }}
                    />
                </NSSection>
            </div>
            </>
        </AccountLayout>
    );
}

export default UserProfilePage;